import { ViewportPortal } from '@xyflow/react';
import type { CSSProperties } from 'react';

import type { LayoutResult, ReferenceChip } from '../layout';
import { ConnectorLabel } from './ConnectorLabel';
import { OwnershipLinePaths } from './OwnershipLinePaths';
import { ReferenceChipButton } from './ReferenceChipButton';
import { UnlinkedTrayHeading } from './UnlinkedTrayHeading';

interface LinesAndLabelsLayerProps {
  layout: Readonly<LayoutResult>;
  highlightedRelationIds: ReadonlySet<string>;
  onChipSelect: (entityId: number) => void;
  onChipReveal: (chip: Readonly<ReferenceChip>) => void;
}

// Below zero so it paints under the entity cards, still inside the viewport the export captures.
const layerStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  zIndex: -1,
};

const LinesAndLabelsLayer = ({
  layout,
  highlightedRelationIds,
  onChipSelect,
  onChipReveal,
}: LinesAndLabelsLayerProps) => (
  <ViewportPortal>
    <div style={layerStyle}>
      {/* Highlighted lines go last so they paint over the lines they share a trunk with. */}
      <OwnershipLinePaths
        lines={layout.lines.filter(
          (line) => !highlightedRelationIds.has(line.relationId)
        )}
        bounds={layout.bounds}
        isHighlighted={false}
      />
      <OwnershipLinePaths
        lines={layout.lines.filter((line) =>
          highlightedRelationIds.has(line.relationId)
        )}
        bounds={layout.bounds}
        isHighlighted
      />
      {layout.labels.map((label) => (
        <ConnectorLabel
          key={label.id}
          rect={label}
          text={label.text}
          isHighlighted={highlightedRelationIds.has(label.relationId)}
        />
      ))}
      {layout.chips.map((chip) => (
        <ReferenceChipButton
          key={chip.id}
          chip={chip}
          isHighlighted={highlightedRelationIds.has(chip.relationId)}
          onSelect={onChipSelect}
          onReveal={onChipReveal}
        />
      ))}
      {layout.tray && <UnlinkedTrayHeading heading={layout.tray.heading} />}
    </div>
  </ViewportPortal>
);

export { LinesAndLabelsLayer };
